import React, { useState, useEffect, useRef } from "react";
import { Layout } from "antd";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import Sidebar from "../Layout/Sidebar";
import CustomHeader from "../Layout/Header";
import SwButton from "../Common/SwButtons/SwButtons";
import SwKpi from "../Common/SwKPI/SwKPI";
import SwKpiGroup from "../Common/SwKPI/SwKpiGroup";
import SwToggleButtonGroup from "../Common/SwToggleButtonGroup/SwToggleButtonGroup";
import SwSelect from "../Common/SwReactSelect/SwReactSelect";
import AntDTable from "../Common/AntDTable/AntDTable";
import SwModal from "../Common/SwModal/SwModal";
import SwBadge from "../Common/SwBadge/SwBadge";
import SwLoader from "../Common/SwLoader/SwLoader";
import axiosInstance from "../../utils/axiosInstance";
import { dataUser } from "../Helpers/util";
import "./CustomerPortal.css";

// Logged-in user (from localStorage `headData`).
const UserObj = (() => {
  try {
    return JSON.parse(dataUser());
  } catch (e) {
    return null;
  }
})();

// The `user` header the backend uses for attribute-based access must carry the
// access-token claims (`old_db_id` + `email`), not the local user record id.
const getUserHeader = () => {
  try {
    const raw = (localStorage.getItem("token") || "").replace(/^Bearer\s+/i, "");
    const payload = raw.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
    return JSON.stringify({ old_db_id: json.old_db_id, email: json.email });
  } catch (e) {
    return null;
  }
};

// cspoc gateway base (e.g. https://api-uat.sworks.co.in/cspoc/) — credit notes
// and disputes are served here.
const CSPOC_BASE = (() => {
  const raw = process.env.REACT_APP_API_GATEWAYS_URL;
  if (!raw) return null;
  return raw.replace(/\/+$/, "") + "/cspoc/";
})();

// Nexus base (e.g. https://uat-nexus-api.sworks.co.in/api/) — invoices (list,
// detail, PDF) and KPIs are served from nexus.
// NOTE: read `REACT_APP_NEXUS_BASE_URL` (not `REACT_APP_API_NEXUS_URL`). The
// CI sed `s|NEXUS_URL|...|` corrupts the latter's key on dev/uat because the
// key itself contains the `NEXUS_URL` substring; the `_BASE_URL` key is immune.
const NEXUS_BASE = (() => {
  const raw =
    process.env.REACT_APP_NEXUS_BASE_URL || process.env.REACT_APP_API_NEXUS_URL;
  if (!raw) return null;
  return raw.replace(/\/+$/, "").replace(/\/api$/i, "") + "/api/";
})();

// Auth for the disputes endpoint: the Keycloak access token (from localStorage
// `token`) sent as the `Authorization` header WITH the `Bearer` prefix.
const getV1AuthToken = () => {
  const raw = (localStorage.getItem("token") || "").replace(/^Bearer\s+/i, "");
  return raw ? `Bearer ${raw}` : undefined;
};

// --- Shared status filter (All / Open / Closed) used on every tab ---
const STATUS_FILTERS = [
  { label: "All", value: "all" },
  { label: "Open", value: "open" },
  { label: "Closed", value: "closed" },
];

// --- Tab definitions ---
const TABS = [
  { key: "invoices", label: "Invoices" },
  { key: "disputes", label: "Disputes" },
  { key: "creditNotes", label: "Credit Notes" },
];

const LIST_PAGE_SIZE = 10;

// --- Formatters --- (all fail safe to "-" / ₹0.00 for bad/missing values)
const formatYMD = (date) => {
  if (!date) return "-";
  const m = moment(date);
  return m.isValid() ? m.format("YYYY-MM-DD") : "-";
};
// ₹ with Indian grouping, e.g. 25189 -> "₹ 25,189.00"
const formatINR = (value) => {
  const n = Number(value);
  return `₹ ${(Number.isFinite(n) ? n : 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Days an invoice is overdue (only when there is an outstanding due amount).
const computeDueDays = (record) => {
  if (!record?.remainingAmount || !record?.dueDate) return "-";
  const days = moment()
    .startOf("day")
    .diff(moment(record.dueDate).startOf("day"), "days");
  return days > 0 ? days : "-";
};

// Reference cell rendered as a button that opens the detail modal.
const refCell = (text, onClick) => (
  <button type="button" className="CustomerPortalInvoiceRef" onClick={onClick}>
    {text || "-"}
    <i className="bi bi-file-earmark-pdf-fill" />
  </button>
);

// Map a textual status to an SwBadge variant.
const statusVariant = (status) => {
  const s = (status || "").toString().toLowerCase();
  if (s.includes("overdue") || s.includes("reject")) return "danger";
  if (
    s.includes("close") ||
    s.includes("paid") ||
    s.includes("settled") ||
    s.includes("resolv")
  )
    return "success";
  if (
    s.includes("open") ||
    s.includes("unpaid") ||
    s.includes("partial") ||
    s.includes("pending")
  )
    return "warning";
  return "info";
};

// A single label/value pair in the detail modal grid.
const DetailField = ({ label, value, strong = false, className = "" }) => (
  <div className={`CustomerPortalDetailField ${className}`}>
    <div className="CustomerPortalDetailLabel">{label}</div>
    <div className={strong ? "CustomerPortalDetailValue strong" : "CustomerPortalDetailValue"}>
      {value ?? "-"}
    </div>
  </div>
);

const makeInvoiceColumns = (onRefClick) => [
  {
    title: "Reference #",
    dataIndex: "invoiceNo",
    key: "invoiceNo",
    sorter: true,
    render: (text, record) => refCell(text, () => onRefClick(record)),
  },
  { title: "Issued Date", dataIndex: "invoiceDate", key: "invoiceDate", sorter: true, render: formatYMD },
  { title: "Invoice Amount", dataIndex: "invoiceAmount", key: "invoiceAmount", sorter: true, render: formatINR },
  { title: "Invoice Due Amount", dataIndex: "remainingAmount", key: "remainingAmount", sorter: true, render: formatINR },
  { title: "Due Date", dataIndex: "dueDate", key: "dueDate", sorter: true, render: formatYMD },
  // Derived from dueDate — sorted server-side via dueDate (see handleInvoiceTableChange).
  { title: "Due Days", key: "dueDays", sorter: true, render: (_, record) => computeDueDays(record) },
];

const makeCreditNoteColumns = (onRefClick, onInvoiceClick) => [
  {
    title: "Credit Note No.",
    dataIndex: "creditNoteId",
    key: "creditNoteId",
    render: (text, record) => refCell(text, () => onRefClick(record)),
  },
  { title: "Date", dataIndex: "creditNoteDate", key: "creditNoteDate", render: formatYMD },
  {
    title: "Invoice No.",
    key: "invoiceNo",
    render: (_, record) =>
      record.invoice?.alternateId ? (
        <button
          type="button"
          className="CustomerPortalAssocInvoiceLink"
          onClick={() => onInvoiceClick(record)}
        >
          {record.invoice.alternateId}
        </button>
      ) : (
        "-"
      ),
  },
  { title: "Amount", dataIndex: "totalAmount", key: "totalAmount", render: formatINR },
];

// A dispute is "Closed" only when its raw status is `rejected` or `done`
// (case-insensitive); every other status is treated as "Open".
const disputeDisplayStatus = (status) => {
  const s = (status || "").toString().trim().toLowerCase();
  return s === "rejected" || s === "done" ? "Closed" : "Open";
};

// Disputes are support tickets (v1 basic-data). Ticket No. links out to the
// ticket's `redirectLink` when present.
const makeDisputeColumns = () => [
  {
    title: "Ticket No.",
    dataIndex: "ticketNumber",
    key: "ticketNumber",
    sorter: true,
    render: (text) => text || "-",
  },
  { title: "Sub Category", dataIndex: "subCategory", key: "subCategory", sorter: true, render: (t) => t || "-" },
  { title: "Subject", dataIndex: "subject", key: "subject", sorter: true, render: (t) => t || "-" },
  { title: "Location", key: "location", render: (_, r) => r.locationName || r.city || "-" },
  { title: "Raised By", key: "raisedBy", render: (_, r) => r.userInfo?.name || "-" },
  {
    // Not sortable: the cell shows the derived Open/Closed label, which would
    // not match a client-side sort on the raw `status` field.
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (s) => {
      const label = disputeDisplayStatus(s);
      return (
        <SwBadge variant={label === "Closed" ? "success" : "warning"} text={label} />
      );
    },
  },
];

function CustomerPortal() {
  const [collapsed, setCollapsed] = useState(true);
  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  const [activeTab, setActiveTab] = useState("invoices");
  const [statusFilter, setStatusFilter] = useState("all");

  // --- Account IDs (for the logged-in user's company) ---
  const companyName = UserObj?.company_name || "-";
  const [accountIds, setAccountIds] = useState([]);
  const [accountLoading, setAccountLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const accountOptions = accountIds.map((a) => ({
    label: `${a.account_id} — ${a.location_name}`,
    value: a.rec_id,
    raw: a,
  }));

  // --- KPIs (per selected account) ---
  const [kpis, setKpis] = useState({
    totalOutstanding: 0,
    currentDue: 0,
    overdue: 0,
  });
  const [kpiLoading, setKpiLoading] = useState(false);

  // --- Tab list data. Invoices are server-paginated; Credit Notes return the
  // full set (paginated client-side by the table). ---
  const [listRows, setListRows] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_PAGE_SIZE);

  // --- Server-side sort (Invoices tab) ---
  const [sortBy, setSortBy] = useState(null); // column dataIndex
  const [sortOrder, setSortOrder] = useState(null); // "asc" | "desc"

  // --- Detail modal (Invoice / Credit Note) ---
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailType, setDetailType] = useState(null); // "invoice" | "creditNote"
  const [detailRecord, setDetailRecord] = useState(null); // the clicked list row
  const [detailData, setDetailData] = useState(null); // enriched data (invoice: fetched)
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false); // invoice fetch failed
  const [pdfLoading, setPdfLoading] = useState(false);

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Guard so the call fires once even with React StrictMode's double-invoke.
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchAccountIds = async () => {
      setAccountLoading(true);
      try {
        const userHeader = getUserHeader();
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_API_GATEWAYS_URL}cspoc/api/v2/basic-data/account-ids`,
          userHeader ? { headers: { user: userHeader } } : undefined
        );
        if (res.data?.status === "success") {
          const list = Array.isArray(res.data.data) ? res.data.data : [];
          setAccountIds(list);
          // Auto-select the first account so KPIs/data populate by default.
          if (list.length) {
            const first = list[0];
            setSelectedAccount({
              label: `${first.account_id} — ${first.location_name}`,
              value: first.rec_id,
              raw: first,
            });
          }
        } else {
          setAccountIds([]);
        }
      } catch (error) {
        console.error("Failed to fetch account IDs:", error);
        toast.error("Unable to load account IDs.");
        setAccountIds([]);
      } finally {
        setAccountLoading(false);
      }
    };

    fetchAccountIds();
  }, []);

  // Fetch outstanding KPIs for the selected account. `ignore` discards a stale
  // response if the account changes / the component unmounts mid-flight.
  useEffect(() => {
    const accountId = selectedAccount?.raw?.account_id;
    if (!accountId) {
      setKpis({ totalOutstanding: 0, currentDue: 0, overdue: 0 });
      return;
    }

    if (!NEXUS_BASE) {
      console.error(
        "REACT_APP_NEXUS_BASE_URL is not set — restart the dev server after adding it to .env.local."
      );
      return;
    }

    let ignore = false;
    const fetchKpis = async () => {
      setKpiLoading(true);
      try {
        const res = await axiosInstance.get(`${NEXUS_BASE}cspoc/ledger/kpis`, {
          params: { accountId },
        });
        if (ignore) return;
        const acc = res.data?.accounts?.[0];
        setKpis({
          totalOutstanding: acc?.totalOutstanding || 0,
          currentDue: acc?.currentDue || 0,
          overdue: acc?.overdue || 0,
        });
      } catch (error) {
        if (ignore) return;
        console.error("Failed to fetch KPIs:", error);
        toast.error("Unable to load outstanding summary.");
        setKpis({ totalOutstanding: 0, currentDue: 0, overdue: 0 });
      } finally {
        if (!ignore) setKpiLoading(false);
      }
    };

    fetchKpis();
    return () => {
      ignore = true;
    };
  }, [selectedAccount]);

  // Changing account / filter / tab resets paging (synchronously, to avoid a
  // wasted fetch on the old page number).
  const handleAccountChange = (option) => {
    setSelectedAccount(option);
    setCurrentPage(1);
  };
  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };
  const handleTabChange = (key) => {
    setActiveTab(key);
    setCurrentPage(1);
  };

  // antd fires this for both page change and page-size change. On a size change
  // we reset to page 1 (and the new limit drives a re-fetch via the effect).
  const handleListPageChange = (page, size) => {
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
    } else {
      setCurrentPage(page);
    }
  };

  // Invoices use server-side sorting: map antd's sorter to sortBy/sortOrder.
  const handleInvoiceTableChange = (_pagination, _filters, sorter) => {
    // `field` for columns with a dataIndex, `columnKey` for computed ones (Due Days).
    const colKey = sorter?.field || sorter?.columnKey;
    let order =
      sorter?.order === "ascend"
        ? "asc"
        : sorter?.order === "descend"
        ? "desc"
        : null;

    // "Due Days" = today − dueDate, so sort by the real dueDate field and invert
    // the direction (ascending day-count ⇒ latest due dates first).
    let field = colKey;
    if (colKey === "dueDays") {
      field = "dueDate";
      if (order) order = order === "asc" ? "desc" : "asc";
    }

    setSortBy(order ? field : null);
    setSortOrder(order);
    setCurrentPage(1);
  };

  // Fetch the active tab's list:
  //  - Invoices    → nexus, server-paginated, supports the all/open/closed filter
  //  - Credit Notes→ cspoc gateway, full set (account-scoped)
  //  - Disputes    → cspoc gateway v1 basic-data, full set (raw axios + Keycloak)
  useEffect(() => {
    const accountId = selectedAccount?.raw?.account_id;

    // Default extract: tolerate `{ data, total }` or a bare array response.
    const defaultExtract = (data) => {
      const payload = Array.isArray(data) ? data : data?.data;
      const rows = Array.isArray(payload) ? payload : [];
      return { rows, total: typeof data?.total === "number" ? data.total : rows.length };
    };

    const ENDPOINTS = {
      invoices: {
        base: NEXUS_BASE,
        path: "cspoc/invoices",
        params: {
          accountId,
          tab: statusFilter,
          page: currentPage,
          limit: pageSize,
          ...(sortBy && sortOrder ? { sortBy, sortOrder } : {}),
        },
      },
      creditNotes: {
        base: CSPOC_BASE,
        path: "getData/credit-note/fetch",
        params: { accountId },
      },
      disputes: {
        base: CSPOC_BASE,
        path: "api/v1/basic-data/get-dispute-list-by-account",
        params: { accountId },
        // Raw axios + the Keycloak token (with `Bearer`) in the Authorization
        // header (see getV1AuthToken).
        raw: true,
        // Tickets are nested at res.data.data.data.
        extract: (data) => {
          const rows = Array.isArray(data?.data?.data) ? data.data.data : [];
          return { rows, total: rows.length };
        },
      },
    };

    const endpoint = ENDPOINTS[activeTab];
    if (!endpoint || !accountId) {
      setListRows([]);
      setListTotal(0);
      return;
    }
    if (!endpoint.base) {
      console.error(
        "API base URL is not set — check the REACT_APP_API_* env vars and restart the dev server."
      );
      return;
    }

    let ignore = false;
    const fetchList = async () => {
      setListLoading(true);
      try {
        const res = endpoint.raw
          ? await axios.get(`${endpoint.base}${endpoint.path}`, {
              params: endpoint.params,
              headers: { Authorization: getV1AuthToken() },
            })
          : await axiosInstance.get(`${endpoint.base}${endpoint.path}`, {
              params: endpoint.params,
            });
        if (ignore) return;
        const { rows, total } = (endpoint.extract || defaultExtract)(res.data);
        setListRows(rows);
        setListTotal(total);
      } catch (error) {
        if (ignore) return;
        console.error(`Failed to fetch ${activeTab}:`, error);
        toast.error("Unable to load data.");
        setListRows([]);
        setListTotal(0);
      } finally {
        if (!ignore) setListLoading(false);
      }
    };

    fetchList();
    return () => {
      ignore = true;
    };
  }, [selectedAccount, statusFilter, activeTab, currentPage, pageSize, sortBy, sortOrder]);

  // --- Detail modal handlers ---
  const closeDetail = () => {
    setDetailOpen(false);
    setDetailType(null);
    setDetailRecord(null);
    setDetailData(null);
    setDetailLoading(false);
    setDetailError(false);
  };

  // Invoices: open the modal immediately, then fetch full details by `_id`.
  const openInvoiceDetail = async (record) => {
    setDetailType("invoice");
    setDetailRecord(record);
    setDetailData(null);
    setDetailError(false);
    setDetailOpen(true);

    const id = record?._id;
    // Use the invoice's own account when available (e.g. the associated invoice
    // opened from a credit note); fall back to the selected account otherwise.
    const accountId =
      record?.account?.alternateId || selectedAccount?.raw?.account_id;
    if (!id || !accountId || !NEXUS_BASE) {
      setDetailError(true);
      return;
    }
    setDetailLoading(true);
    try {
      const res = await axiosInstance.get(`${NEXUS_BASE}cspoc/invoices/${id}`, {
        params: { accountId },
      });
      const data = res.data?.data || res.data || null;
      if (!data) {
        setDetailError(true);
      } else {
        setDetailData(data);
      }
    } catch (error) {
      console.error("Failed to fetch invoice details:", error);
      setDetailError(true);
    } finally {
      setDetailLoading(false);
    }
  };

  // Credit notes: all detail fields are already present in the list row.
  const openCreditNoteDetail = (record) => {
    setDetailType("creditNote");
    setDetailRecord(record);
    setDetailData(record);
    setDetailOpen(true);
  };

  // Open the invoice associated with a credit note row. The invoice carries its
  // own `_id` + `account` (used for the detail fetch / accountId).
  const openAssociatedInvoice = (creditNote) => {
    if (!creditNote?.invoice?.alternateId) return;
    openInvoiceDetail({
      ...creditNote.invoice,
      account: creditNote.invoice?.account || creditNote.account,
    });
  };

  // Download a PDF from its public (S3) `pdfName` URL and save it to disk.
  // NOTE: we use a bare `fetch` — NOT axiosInstance — because axiosInstance
  // attaches the Keycloak `Authorization` header, which S3 rejects with 400.
  // Falls back to opening the file directly if a CORS policy blocks the blob.
  const downloadPdf = async (pdfUrl, fileName) => {
    if (!pdfUrl) {
      toast.error("PDF is not available.");
      return;
    }
    setPdfLoading(true);
    try {
      const resp = await fetch(pdfUrl);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("Failed to download PDF:", error);
      // CORS / network failure — open directly so the file is still reachable.
      window.open(pdfUrl, "_blank", "noopener");
    } finally {
      setPdfLoading(false);
    }
  };

  // Invoice PDF: nexus endpoint with invoiceNo + accountId as query params.
  // Fetch it (auth header required) and open the resulting file in a new tab.
  const handleDownloadInvoicePdf = async () => {
    const invoiceNo =
      detailData?.invoiceNo || detailData?.alternateId || detailRecord?.invoiceNo;
    // Match the account used to fetch the detail (associated invoices carry their own).
    const accountId =
      detailRecord?.account?.alternateId || selectedAccount?.raw?.account_id;
    if (!invoiceNo || !accountId || !NEXUS_BASE) {
      toast.error("Invoice PDF is not available.");
      return;
    }
    setPdfLoading(true);
    try {
      const res = await axiosInstance.get(`${NEXUS_BASE}cspoc/invoices/pdf`, {
        params: { invoiceNo, accountId },
        responseType: "blob",
      });
      const blob = res.data;
      // The endpoint may return JSON `{ url }` instead of the binary PDF.
      if (blob?.type?.includes("application/json")) {
        const json = JSON.parse(await blob.text());
        const url = json.url || json.data?.url || json.pdfName;
        if (url) window.open(url, "_blank", "noopener");
        else toast.error("Invoice PDF is not available.");
        return;
      }
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("Failed to load invoice PDF:", error);
      toast.error("Unable to load invoice PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  // Credit note: force-download the file to disk.
  const handleDownloadCreditNotePdf = () =>
    downloadPdf(
      cn.pdfName,
      `${cn.creditNoteId || cn.referenceNumber || "credit-note"}.pdf`
    );

  // Every tab is API-backed.
  const invoiceColumns = makeInvoiceColumns(openInvoiceDetail);
  const creditNoteColumns = makeCreditNoteColumns(
    openCreditNoteDetail,
    openAssociatedInvoice
  );
  const disputeColumns = makeDisputeColumns();
  const activeColumns =
    activeTab === "invoices"
      ? invoiceColumns
      : activeTab === "creditNotes"
      ? creditNoteColumns
      : activeTab === "disputes"
      ? disputeColumns
      : [];

  // Merged invoice view: list row enriched with the fetched detail response.
  const inv = { ...(detailRecord || {}), ...(detailData || {}) };
  // Credit note view: the list row already carries every field.
  const cn = detailData || detailRecord || {};

  const handleRaiseDispute = () => {
    // Stub — behavior to be defined later.
  };

  return (
    <>
      <Layout>
        <Sidebar collapsed={collapsed} />

        <CustomHeader collapsed={collapsed} toggleCollapsed={toggleCollapsed} />

        <Layout className={collapsed ? "PageLayout CollapsedPageLayout" : "PageLayout"}>
          <div className="PageContent">
            {/* Header: title + Raise Dispute */}
            <div className="row mb-3 align-items-center">
              <div className="col-md-6">
                <div className="SwHeading">
                  <h2>Customer Portal</h2>
                </div>
              </div>
              {/* <div className="col-md-6">
                <div className="PageHead_Optons_Row">
                  <ul className="PageHead_Optons_RightAligned">
                    <li>
                      <SwButton
                        variant="primary"
                        size="sm"
                        onClick={handleRaiseDispute}
                      >
                        Raise Dispute
                      </SwButton>
                    </li>
                  </ul>
                </div>
              </div> */}
            </div>

            {/* Company name + Account ID selector */}
            <div className="row mb-3 align-items-end g-3">
              <div className="col-md-4">
                <div className="SwFormGroup mb-0">
                  <label className="SwFormLabel">Company Name</label>
                  <div className="CustomerPortalCompanyName">{companyName}</div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="SwFormGroup mb-0">
                  <label className="SwFormLabel">Account ID</label>
                  <SwSelect
                    options={accountOptions}
                    value={selectedAccount}
                    onChange={handleAccountChange}
                    isLoading={accountLoading}
                    isClearable={false}
                    placeholder="Select Account ID"
                  />
                </div>
              </div>
            </div>

            {/* KPI cards */}
            <div className="row mb-3 CustomerPortalKpis">
              <div className="col-12">
                <SwKpiGroup>
                  <SwKpi
                    label="Total Outstanding"
                    value={formatINR(kpis.totalOutstanding)}
                    loading={kpiLoading}
                    tooltip="Total amount currently outstanding across all invoices."
                  />
                  <SwKpi
                    label="Current Due"
                    value={formatINR(kpis.currentDue)}
                    loading={kpiLoading}
                    tooltip="Amount due within the current billing period."
                  />
                  <SwKpi
                    label="Over Due"
                    value={formatINR(kpis.overdue)}
                    loading={kpiLoading}
                    tooltip="Amount past its due date."
                  />
                </SwKpiGroup>
              </div>
            </div>

            {/* Tabs */}
            <div className="row">
              <div className="col-12">
                <div className="CustomerPortalTabs">
                  {TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={
                        activeTab === tab.key
                          ? "CustomerPortalTab active"
                          : "CustomerPortalTab"
                      }
                      onClick={() => handleTabChange(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* All / Open / Closed filter — only the Invoices endpoint supports it */}
            {activeTab === "invoices" && (
              <div className="row mt-3">
                <div className="col-12">
                  <SwToggleButtonGroup
                    type="radio"
                    name="customerPortalStatus"
                    value={statusFilter}
                    onChange={handleStatusChange}
                    size="sm"
                    options={STATUS_FILTERS}
                  />
                </div>
              </div>
            )}

            {/* Table changes with the active tab */}
            <div className="row">
              <div className="col-12">
                <div className="mt-2">
                  <AntDTable
                    columns={activeColumns}
                    dataSource={listRows}
                    rowKey={(record, index) => record._id ?? index}
                    loading={listLoading}
                    pagination
                    pageSize={pageSize}
                    currentPage={currentPage}
                    totalRecords={listTotal}
                    onPageChange={handleListPageChange}
                    // Invoices sort server-side (sortBy/sortOrder); Credit Notes &
                    // Disputes sort client-side. Invoices show sort icons; the
                    // others' columns drive their own icon visibility.
                    clientSideSorting={activeTab !== "invoices"}
                    onChange={
                      activeTab === "invoices" ? handleInvoiceTableChange : undefined
                    }
                    scroll={{ x: 1000 }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Layout>
      </Layout>

      {/* Invoice / Credit Note detail modal */}
      <SwModal
        show={detailOpen}
        onClose={closeDetail}
        size="lg"
        title={
          <span className="CustomerPortalModalTitle">
            <i className="bi bi-file-earmark-text" />
            {detailType === "creditNote" ? "Credit Note Details" : "Invoice Details"}
          </span>
        }
        footer={
          detailError ? null : detailType === "invoice" &&
            (inv.invoiceNo || inv.alternateId) ? (
            <SwButton
              variant="dark-outline"
              size="sm"
              onClick={handleDownloadInvoicePdf}
              loading={pdfLoading}
              icon={<i className="bi bi-download" />}
            >
              Download PDF
            </SwButton>
          ) : detailType === "creditNote" && cn.pdfName ? (
            <SwButton
              variant="dark-outline"
              size="sm"
              onClick={handleDownloadCreditNotePdf}
              loading={pdfLoading}
              icon={<i className="bi bi-download" />}
            >
              Download PDF
            </SwButton>
          ) : null
        }
      >
        {detailLoading ? (
          <div className="CustomerPortalDetailLoading">
            <SwLoader />
          </div>
        ) : detailError ? (
          <div className="CustomerPortalDetailError">
            <i className="bi bi-exclamation-circle" />
            <p>
              {detailType === "invoice"
                ? "Unable to load invoice details. The invoice could not be found."
                : "Unable to load details."}
            </p>
          </div>
        ) : detailType === "invoice" ? (
          <div className="CustomerPortalDetailGrid row g-4">
            <div className="col-6">
              <DetailField label="Title" value={inv.invoiceNo || inv.alternateId} strong />
            </div>
            <div className="col-6">
              <DetailField
                label="Status"
                value={
                  inv.status || inv.paymentStatus ? (
                    <SwBadge
                      variant={statusVariant(inv.status || inv.paymentStatus)}
                      text={inv.status || inv.paymentStatus}
                    />
                  ) : (
                    "-"
                  )
                }
              />
            </div>
            <div className="col-6">
              <DetailField label="Issue Date" value={formatYMD(inv.invoiceDate)} />
            </div>
            <div className="col-6">
              <DetailField label="Due Date" value={formatYMD(inv.dueDate)} />
            </div>
            <div className="col-6">
              <DetailField
                label="Balance"
                value={formatINR(inv.remainingAmount ?? inv.balance)}
                strong
              />
            </div>
            <div className="col-6">
              <DetailField
                label="Total Amount"
                value={formatINR(inv.invoiceAmount ?? inv.totalAmount)}
                strong
              />
            </div>
          </div>
        ) : detailType === "creditNote" ? (
          <div className="CustomerPortalDetailGrid row g-4">
            <div className="col-6">
              <DetailField
                label="Reference Number"
                value={cn.creditNoteId || cn.referenceNumber}
                strong
              />
            </div>
            <div className="col-6">
              <DetailField label="Credit Date" value={formatYMD(cn.creditNoteDate)} />
            </div>
            <div className="col-6">
              <DetailField
                label="Credit Amount"
                value={formatINR(cn.totalAmount ?? cn.creditAmount)}
              />
            </div>
            <div className="col-6">
              <DetailField
                label="Associated Invoice"
                value={
                  cn.invoice?.alternateId ? (
                    <span className="CustomerPortalAssocInvoice">
                      <button
                        type="button"
                        className="CustomerPortalAssocInvoiceLink"
                        onClick={() => openAssociatedInvoice(cn)}
                      >
                        {cn.invoice.alternateId}
                      </button>
                      {(cn.invoice?.amount ?? cn.appliedAmount) != null && (
                        <span className="CustomerPortalAssocInvoiceAmt">
                          {formatINR(cn.invoice?.amount ?? cn.appliedAmount)}
                        </span>
                      )}
                    </span>
                  ) : (
                    "-"
                  )
                }
              />
            </div>
          </div>
        ) : null}
      </SwModal>
    </>
  );
}

export default CustomerPortal;
